/* eslint-disable react/prop-types */
import { createContext, useState, useContext, useEffect, useRef } from 'react';

import { getGridProductsById } from 'actions/products';
import { useI18n } from './i18n';

import type { TProductData } from 'types/product-list';

type TProductsGridContext = {
  productData: Record<string, TProductData>;
  getSingleProductData: (productId: string, brandProductId: string) => TProductData;
  addProductData: (productId: string, productData: TProductData) => void;
};

const ProductsGridContext = createContext<TProductsGridContext | undefined>(undefined);

type TProductsGridContextProps = {
  children: React.ReactNode;
  data: TProductData[];
  squareImg: boolean;
};

export function ProductsGridContextProvider({ children, data, squareImg }: TProductsGridContextProps) {
  const { locale } = useI18n();
  const initialProductData = {};
  const loadingColors = useRef(true);

  data.forEach((initialProduct) => {
    initialProductData[initialProduct.id] = { ...initialProduct, item: initialProduct };
  });

  const productData = useRef(initialProductData);

  const addProductData = (productId: string, data: TProductData) => {
    productData.current[productId] = data;
  };

  const getSingleProductData = (productId: string, brandProductId: string) => {
    try {
      const product = productData.current[brandProductId];

      if (product) {
        return product;
      }

      throw new Error(`No product for ID ${brandProductId}. This should not happen.`);
    } catch (e) {
      console.error(e);

      return productData.current[productId];
    }
  };

  useEffect(() => {
    const handleGetColors = async () => {
      const realTargetId = data[0].targetId;
      const colorIDs = Array.from(
        new Set(
          data
            .filter((item) => item.colors)
            .map((item) => item.colors)
            .flatMap((item) => item)
            .map((item) => item.id)
            .filter((item) => !Object.keys(productData).includes(item)),
        ),
      );

      const productResponse = await getGridProductsById({ profile: locale, target: realTargetId, skus: colorIDs, square: squareImg });

      if (productResponse) {
        productResponse
          .filter((productItem) => !(productItem.id in productData))
          .forEach((productItem) => {
            addProductData(productItem.id, { ...productItem, item: productItem });
          });
      }
    };

    if (loadingColors.current) {
      loadingColors.current = false;
      handleGetColors();
    }
  }, [loadingColors.current]);

  useEffect(() => {
    loadingColors.current = true;
  }, [data]);

  return (
    <ProductsGridContext.Provider
      value={{
        productData: productData.current,
        getSingleProductData,
        addProductData,
      }}
    >
      {children}
    </ProductsGridContext.Provider>
  );
}

export function useProductsGrid() {
  const context = useContext(ProductsGridContext);
  if (context === undefined) {
    throw new Error('useProductsGrid must be used within a ProductsGridContextProvider');
  }
  return context;
}
